import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { BaseProductDetailComponent } from '@components/products/base-product-detail.component';
import { FundDetailService } from '@components/products/services/fund-detail.service';
import {
  FundInformationOverview, FundPerformanceAnnualizedState,
  FundPerformanceCummulativeState, FundPerformanceTableContent, FundPerformanceTableData, LiteratureDTO,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundPerformanceComponentConfig } from '../fund-performance-component.config';
import { FundPerformanceDisplayComponent } from '../fund-performance-display-component.interface';
import * as Highcharts from 'highcharts';
import { FundPerformanceCummulativeService } from '../services/fund-performance-cummulative.service';
import {
  HighchartsThemeService
} from '@components/shared/services/highchart/highcharts-theme/highcharts-theme.service';
import moment from 'moment';
import { FundPerformanceAnnualizedService } from '../services/fund-performance-annualized.service';
import { FundPerformanceAnnualizedDirectService } from '../services/fund-performance-annualized-direct.service';
import { FundDocumentsService } from "@products/other-details/fund-documents/services/fund-documents.service";
import { CaveatService } from "@products/caveats/services/caveat.service";
import filter from 'lodash/filter';
import { SiteConfigService } from "@services/site-config.service";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from '@components/shared/translate/translate.service';

const logger = Logger.getLogger('FundPerformanceCumulativeComponent');

export interface PerformanceModel {
  investedAmount: number;
  toDate: string;
  fromDate: string;
  maxInvestedAmt: number;
  sipInvestedAmount: number;
  potentialRtn: number;
  inceptionDate: string;
  formattedToDate: string;
}

@Component({
  selector: 'ft-fund-performance-cumulative',
  templateUrl: './fund-performance-cumulative.component.html',
  styleUrls: ['./fund-performance-cumulative.component.scss'],
})
export class FundPerformanceCumulativeComponent
  extends BaseProductDetailComponent
  implements FundPerformanceDisplayComponent, OnInit, AfterViewInit, OnDestroy {
  dataSubscriptions: Subscription[];
  isLumpsumActive = false;
  isSIPActive = true;
  chart: any;
  options: any;
  isChartReady = false;
  @ViewChild('container') public chartEl: ElementRef;
  @ViewChild('formatButtons') public activeSelection: ElementRef;
  overview: FundInformationOverview;
  isLoaded = false;
  performanceData: FundPerformanceCummulativeState;
  showError = false;
  performanceModel: PerformanceModel = {
    investedAmount: 10000,
    fromDate: '',
    toDate: '',
    maxInvestedAmt: null,
    potentialRtn: null,
    sipInvestedAmount: null,
    inceptionDate: '',
    formattedToDate: '',
  };
  perfAsOfDate: string;
  sinceInceptionReturnDate: string;
  lumpsumDataAnnualized: FundPerformanceTableContent;
  lumpsumDataCumulative: FundPerformanceTableContent;
  lumpsumDataDiscrete: FundPerformanceTableContent;
  sipData: FundPerformanceTableData[];
  hasLoaded: boolean;
  perfAsOfDateDirect: string;
  sinceInceptionReturnDateDirect: string;
  lumpsumDataAnnualizedDirect: FundPerformanceTableContent;
  lumpsumDataCumulativeDirect: FundPerformanceTableContent;
  lumpsumDataDiscreteDirect: FundPerformanceTableContent;
  sipDataDirect: FundPerformanceTableData[];
  hasLoadedDirect: boolean;
  linkFromLiteratureService = {
    buttonLink: undefined,
    textLink: undefined,
  };
  graphDataLoaded: boolean = false;
  annualizedCaveats: string;
  showSipData = true;
  showDiscretePerformance = false;
  showSipDataDirect = true;
  showDiscretePerformanceDirect = false;
  isSegregated: boolean;
  isIndiaValueFund: boolean;
  regularPerformanceLabel: string;
  listOfTabs: string[] = [];
  disableTab: boolean = false;
  sipLumpError: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private fundDetailService: FundDetailService,
    private cummulativeService: FundPerformanceCummulativeService,
    private siteConfig: SiteConfigService,
    private highchartsTheme: HighchartsThemeService,
    private caveatService: CaveatService,
    private fundPerformanceAnnualizedService: FundPerformanceAnnualizedService,
    private fundPerformanceAnnualizedDirectService: FundPerformanceAnnualizedDirectService,
    private fundService: FundDocumentsService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
  ) {
    super();
  }

  ngAfterViewInit() {
    this.route.queryParams?.subscribe((queryParams) => {
      const scrollToPerf = queryParams.scrolltoperf;
      if (scrollToPerf) {
        this.scrollToPerf();
      }
    })
  }

  ngOnInit() {
    this.loadGraphData('SIP');
  }

  populate(
    componentConfig: FundPerformanceComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);

    this.cdr.detectChanges();

    if (this.brConfig.fundId) {
      const tabSet = this.siteConfig.getFundTabSet(this.brConfig.fundId);
      this.listOfTabs = tabSet.tabs;
      this.isIndiaValueFund = this.brConfig.fundId === '629'; 
      this.regularPerformanceLabel = this.isIndiaValueFund
      ? this.translateService.instant(
        'products.performance-regular-idcw-plan'
      ) : this.translateService.instant(
        'products.performance-regular-plan'
      )
      this.isSegregated = this.siteConfig.isSegregatedFund(
        this.brConfig.fundId
      );
      this.dataSubscriptions = [];
      if (!this.isSegregated && this.listOfTabs.includes('performance')) {
        this.dataSubscriptions.push(
          this.cummulativeService
            .getCummulativeState$()
            .subscribe(
              (state: FundPerformanceCummulativeState) => {
                this.isLoaded = state.isLoaded;
                if (this.isLoaded) {
                  this.performanceData = state;
                  this.fundPerformanceInit();
                }
              },
              (error) => {
                logger.error(
                  `Error in invoking fund information service: ${error}`
                );
                this.isLoaded = false;
              }
            )
        );
        this.cummulativeService.populate(brConfig);

        this.dataSubscriptions.push(
          this.fundService
            .getFundLiteratureDocument$(
              '?channel=en-in&cache=true&frkOneTISNumber=' + this.brConfig.fundId
            )
            ?.subscribe(
              (data: LiteratureDTO): void => {
                const buttonLink = filter(data.document, {
                  contentGrouping: 'PERFORMANCE-LUMPSUM-FS',
                });
                const textLink = filter(data.document, {
                  contentGrouping: 'PORTFOLIO-PERFORMANCE-FS',
                });

                this.linkFromLiteratureService = {
                  textLink: textLink[0]?.literatureHref
                    ? 'download' + textLink[0]?.literatureHref
                    : textLink[0]?.literatureHref,
                  buttonLink: buttonLink[0]?.literatureHref
                    ? 'download' + buttonLink[0]?.literatureHref
                    : buttonLink[0]?.literatureHref,
                };

                this.cdr.detectChanges();
              },
              (err) => {
                logger.debug('Literature HTTP Error', err);
              }
            )
        );

        this.dataSubscriptions.push(
          this.fundPerformanceAnnualizedService.getAnnualizedState$().subscribe(
            (state: FundPerformanceAnnualizedState) => {
              if (state.isLoaded) {
                this.perfAsOfDate = state.perfAsOfDate;
                this.sinceInceptionReturnDate = state.sinceInceptionReturnDate;
                this.lumpsumDataAnnualized = state.lumpsumData.annualizedCurrent;
                this.lumpsumDataCumulative = state.lumpsumData?.cumulative;
                this.showDiscretePerformance = state.showDiscretePerformance;
                this.lumpsumDataDiscrete = state.lumpsumData?.discrete;

                this.sipData = state.sipData;
                this.hasLoaded = state.perfAsOfDate !== undefined;
              } else {
                this.hasLoaded = false;
              }

              this.cdr.detectChanges();
            },
            (error) => {
              logger.error(
                `Error in invoking fund performance annualized service: ${error}`
              );

              this.hasLoaded = false;
              this.cdr.detectChanges();
            }
          )
        );

        this.dataSubscriptions.push(
          this.fundPerformanceAnnualizedDirectService.getAnnualizedState$().subscribe(
            (state: FundPerformanceAnnualizedState) => {
              if (state.isLoaded) {
                this.perfAsOfDateDirect = state.perfAsOfDate;
                this.sinceInceptionReturnDateDirect = state.sinceInceptionReturnDate;
                this.lumpsumDataAnnualizedDirect = state.lumpsumData.annualizedCurrent;
                this.lumpsumDataCumulativeDirect = state.lumpsumData?.cumulative;
                this.showDiscretePerformanceDirect = state.showDiscretePerformance;
                this.lumpsumDataDiscreteDirect = state.lumpsumData?.discrete;

                this.sipDataDirect = state.sipData;
                this.hasLoadedDirect = state.perfAsOfDate !== undefined;
              } else {
                this.hasLoadedDirect = false;
              }

              this.cdr.detectChanges();
            },
            (error) => {
              logger.error(
                `Error in invoking fund performance annualized service: ${error}`
              );

              // this.hasLoaded = false;
              this.cdr.detectChanges();
            }
          )
        );

        this.dataSubscriptions.push(
          this.fundPerformanceAnnualizedService
            .getSipDataVisibilityUpdate$()
            .subscribe((showSipData) => {
              this.showSipData = showSipData;
              this.cdr.detectChanges();
            })
        );

        this.dataSubscriptions.push(
          this.fundPerformanceAnnualizedDirectService
            .getSipDataVisibilityUpdate$()
            .subscribe((showSipData) => {
              this.showSipDataDirect = showSipData;
              this.cdr.detectChanges();
            })
        );
        this.dataSubscriptions.push(
          this.caveatService
            .getLegals$('FundAnnualizedTable')
            .subscribe((caveats: string[]) => {
              this.annualizedCaveats = caveats.join(' ');
              this.cdr.detectChanges();
            })
        );

        this.fundPerformanceAnnualizedService.populate(brConfig);
        this.fundPerformanceAnnualizedDirectService.populate(brConfig);    //For DIRECT Plan performances
      } else {
        this.disableTab = true;
      }
    }

  }

  fundPerformanceInit() {
    const currentDate = new Date();
    const fromDate = moment(this.performanceData.inceptionDate, 'DD-MM-YYYY');
    this.performanceModel.inceptionDate = moment(fromDate)
      .format('MMM YYYY')
      ?.toUpperCase();
    this.performanceModel.fromDate = moment(fromDate)
      .format('MMM YYYY')
      ?.toUpperCase();
    this.performanceModel.formattedToDate = moment(
      currentDate.setMonth(currentDate.getMonth() - 1)
    ).format('DD-MM-YYYY');
    const toDate = moment(this.performanceModel.formattedToDate, 'DD-MM-YYYY');
    this.performanceModel.toDate = moment(toDate)
      .format('MMM YYYY')
      ?.toUpperCase();
    this.cdr.detectChanges();
    this.loadGraphData('SIP');
  }

  getChartOptions() {
    Highcharts.setOptions({
      lang: {
        thousandsSep: ',',
      },
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Hanken Grotesk,sans-serif'
        }
      }
    });
    return {
      colors: this.highchartsTheme.lineGraphThemeColors,
      chart: {
        type: 'areaspline',
        height: 403,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      yAxis: {
        title: {
          text: 'Value Of Investment(&#8377)',
          style: {
            color: this.highchartsTheme.themeFonts.colors.orange,
            font: this.highchartsTheme.themeFonts.fontFamilyVerdana,
            fill: this.highchartsTheme.themeFonts.colors.orange,
            fontWeight: '500',
          },
        },
        labels: {
          style: {
            color: '#666666',
          },
        },
        tickWidth: 1,
      },
      xAxis: {
        startOnTick: true,
        labels: {
          style: {
            color: '#666666',
          },
        },
        title: {
          text: 'Investment Period',
          style: {
            color: this.highchartsTheme.themeFonts.colors.orange,
            font: this.highchartsTheme.themeFonts.fontFamilyVerdana,
            fill: this.highchartsTheme.themeFonts.colors.orange,
            fontWeight: '500',
          },
        },
        tickWidth: 1,
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'top',
        borderWidth: 0,
        enabled: true,
        x: 10,
        y: 20,
        alignColumns: false,
        itemStyle: {color: '#666666', fontWeight: 'bold'},
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.3,
          threshold: 15
        },
        series: {
          lineWidth: 2
        }
      },
      series: [],
    };
  }

  populateLineChart(data) {
    this.options = this.getChartOptions();
    const updatedData = data?.body?.calculatorDateWiseResp[0]?.returnData;
    updatedData.forEach((row, index) => {
      const tempRow = {
        name: row.returnType,
        data: this.getDataForChart(row),
      };
      this.options.series[index] = tempRow;
    });
    this.options.xAxis.categories = this.getCategories(updatedData[0]);
    this.isChartReady = true;
    this.chart = Highcharts.chart(this.chartEl.nativeElement, this.options);
    const periodicDataLength = updatedData[0]?.periodicReturnData?.length;
    const finalRow = updatedData[0]?.periodicReturnData[periodicDataLength - 1];
    this.performanceModel.maxInvestedAmt = finalRow?.valueOfAmount?.replace(
      /,/g,
      ''
    );
    this.performanceModel.sipInvestedAmount = parseInt(
      finalRow?.investedAmount?.replace(/,/g, ''),
      10
    );
    this.performanceModel.potentialRtn = this.performanceModel.maxInvestedAmt - this.performanceModel.sipInvestedAmount;
    this.cdr.detectChanges();
  }

  getCategories(data) {
    const categories = [];
    data?.periodicReturnData.forEach((row) => {
      categories.push(row?.period);
    });
    return categories;
  }

  getDataForChart(row) {
    const updatedData1 = [];
    let updatedData = [];
    row?.periodicReturnData.forEach((data) => {
      const amount = parseFloat(data?.valueOfAmount?.replace(/,/g, ''));
      updatedData = [amount];
      updatedData1.push(updatedData);
    });
    return updatedData1;
  }

  getRequestObject(trxnType) {
    const req: any = {
      calculatorDateWiseReq: [],
    };
    const innerReq: any = {
      trxnType,
      fundName: this.brConfig.fundId,
      fromDate: this.performanceModel.fromDate.replace(/ /, ''),
      toDate: this.performanceModel.toDate.replace(/ /, ''),
    };
    if (trxnType === 'SIP') {
      innerReq.investmentAmount = this.performanceModel.investedAmount;
    } else {
      innerReq.lumpsumInvestmentAmount = this.performanceModel.investedAmount;
    }
    req.calculatorDateWiseReq.push(innerReq);
    return req;
  }

  reset() {
    this.performanceModel.maxInvestedAmt = null;
    this.performanceModel.potentialRtn = null;
    this.performanceModel.sipInvestedAmount = null;
    this.isChartReady = false;
  }

  loadGraphData(trxnType) {
    this.reset();
    if (this.performanceModel.fromDate) {
      this.graphDataLoaded = false;
      this.fundDetailService
        .getFundPerformanceData(this.getRequestObject(trxnType))
        .subscribe((data) => {
            this.graphDataLoaded = true;
            this.sipLumpError = false;
            this.cdr.detectChanges();
            this.populateLineChart(data);
          },
          error => {
            this.sipLumpError = true;
            this.graphDataLoaded = true;
            console.error("sipLumpPerfCalculator:: ", error.error[0].errorDescription);
            this.cdr.detectChanges();
          });
    }
  }

  onClickOfSIPOrLumpsum(event) {
    const trxnType = event.target.value;
    this.performanceModel.investedAmount = trxnType === 'SIP' ? 10000 : 5000;
    this.sipLumpError = false;
    if (!this.isNotValid()) {
      this.loadGraphData(trxnType);
    }

    // Trigger change in table structure & data as per toggle between lumpsum and sip.
    trxnType === 'SIP'
      ? this.fundPerformanceAnnualizedService.setSipDataVisibility(true)
      : this.fundPerformanceAnnualizedService.setSipDataVisibility(false);
    trxnType === 'SIP'
      ? this.fundPerformanceAnnualizedDirectService.setSipDataVisibility(true)
      : this.fundPerformanceAnnualizedDirectService.setSipDataVisibility(false);      
  }

  isNotValid() {
    const investedAmount =
      typeof this.performanceModel.investedAmount === 'string'
        ? parseInt(this.performanceModel.investedAmount, 10)
        : this.performanceModel.investedAmount;
    if (isNaN(investedAmount) || investedAmount < 5000) {
      this.showError = true;
    } else {
      this.showError = false;
    }
    return this.showError;
  }

  onInvestedAmountChange() {
    if (!this.isNotValid()) {
      this.onInputChange();
    }
  }

  onInputChange() {
    let activeSelection: any;
    this.activeSelection.nativeElement.childNodes.forEach((item) => {
      if (item.classList.contains('activeFormat')) {
        activeSelection = item;
      }
    });
    let txnType = activeSelection?.value;
    if (!txnType) {
      txnType = 'Lumpsum';
    }
    this.loadGraphData(txnType);
  }

  onFinalDate(selectedDate, dateField) {
    if (
      dateField === 'fromDate' &&
      this.performanceModel.fromDate !== selectedDate
    ) {
      this.performanceModel.fromDate = selectedDate;
      this.onInputChange();
    } else if (
      dateField === 'toDate' &&
      this.performanceModel.toDate !== selectedDate
    ) {
      this.performanceModel.toDate = selectedDate;
      this.onInputChange();
    }

    this.cdr.detectChanges();
  }

  private scrollToPerf() {
    const elementToScroll = document.getElementById('performance');
    if (elementToScroll) {
      setTimeout(() => {
        elementToScroll.scrollIntoView({behavior: "smooth", block: "start"});
      }, 3000);
    }
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    if (this.dataSubscriptions) {
      for (const sub of this?.dataSubscriptions) {
        sub.unsubscribe();
      }
    }
  }
}
